import { applyNodeChanges, type NodeChange, type Viewport } from '@xyflow/react';
import { produce } from 'immer';
import { partition } from 'lodash';
import { type ReactNode } from 'react';
import { create } from 'zustand';

import { type UseTrackCanvasSmartResizeGeneratedParams } from '@air/analytics';

import { type CanvasToolRailToolId } from './components/CanvasToolRail/canvasToolRailCatalog';
import { useCommandHistory } from './hooks/commands/hooks/useCommandHistory';
import { type FormatIdStrip } from './hooks/commands/types';
import { getNodeData } from './hooks/sync/getNodeData';
import { type GroupMutations, type NodeUpdate } from './hooks/sync/useCanvasSync';
import { type ZoomToFitParams } from './hooks/useZoomToFit';
import { type ZoomToFitIfNeededParams } from './hooks/useZoomToFitIfNeeded';
import { bindEditCanvasBridge } from './localization/editCanvasBridge';
import { useLocalizationStore } from './localization/localizationStore';
import { type SmartResizeFormat } from './nodes/Image/smartResizeShared';
import { type Orientation } from './nodes/Image/transforms/rotateFlip/orientationMaps';
import { TRANSFORM_STORE_CONFIGS } from './nodes/Image/transforms/storeConfigs';
import {
  type ActiveTool,
  type AppNode,
  type BoundingBox,
  type CanvasExportOptions,
  type CanvasMode,
  type CanvasProposedImage,
  type GetEditUploadUrlFn,
  type SaveCompositeParams,
  type SaveVersionBoardScope,
} from './types';
import {
  buildCropSession,
  buildGroupCropSession,
  buildMixedCropSession,
  buildMultiGroupCropSession,
  CENTER_CROP_BOX_ANCHOR,
  type GroupCropFrame,
  getNodeDisplaySize,
  groupFramePixelSize,
  inferCropBoxAnchor,
  type PixelSize,
  syncCropBoxesToTargetSize,
  targetSizeFromBox,
} from './utils/buildCropSession';
import { computeGroupBounds, getGroupMembers } from './utils/group/nodeGrouping';
import { partitionCropTargets } from './utils/group/resolveCropGroupId';
import { type SnapLine } from './utils/nodeSnapping';
import { getResizeSnapTarget } from './utils/resizeSnapTarget';

/** Lift the focused crop image above its neighbors so shared-edge handles stay hittable. */
const CROP_FOCUS_Z_INDEX = 10_000;

type CropZIndexSnapshot = Record<string, number | undefined>;

export type SmartResizeSource = UseTrackCanvasSmartResizeGeneratedParams['source'];

export type PendingAutoSmartResize = {
  formats: SmartResizeFormat[];
  nodeId: string;
  /** The saved set the formats came from, for analytics. */
  sizeSetId?: string;
  source: SmartResizeSource;
};

export type PendingSmartResizeHandoff = {
  nodeId: string;
  /** Saved set the picker preselects. */
  sizeSetId?: string;
  source: SmartResizeSource;
};

export type HasEverHadNodesVisit = {
  hasEverHadNodes: boolean;
  visitCanvasId: string;
};

const snapshotCropZIndexes = (
  existing: CropZIndexSnapshot | null,
  nodes: AppNode[],
  cropNodeIds: string[],
): CropZIndexSnapshot => {
  if (existing) {
    return existing;
  }

  const snapshot: CropZIndexSnapshot = {};

  for (const id of cropNodeIds) {
    snapshot[id] = nodes.find((node) => node.id === id)?.zIndex;
  }

  return snapshot;
};

const applyCropFocusZIndex = (
  nodes: AppNode[],
  cropNodeIds: string[],
  focusId: string,
  snapshot: CropZIndexSnapshot | null,
): AppNode[] => {
  const zIndexes = snapshot ?? {};

  return nodes.map((node) => {
    if (!cropNodeIds.includes(node.id)) {
      return node;
    }

    return {
      ...node,
      zIndex: node.id === focusId ? CROP_FOCUS_Z_INDEX : zIndexes[node.id],
    };
  });
};

const restoreCropZIndexes = (nodes: AppNode[], snapshot: CropZIndexSnapshot | null): AppNode[] => {
  if (!snapshot) {
    return nodes;
  }

  return nodes.map((node) => (node.id in snapshot ? { ...node, zIndex: snapshot[node.id] } : node));
};

const memberIdSet = (nodes: AppNode[], groupId: string): Set<string> =>
  new Set(getGroupMembers(nodes, groupId).map((node) => node.id));

const groupCropFrames = (nodes: AppNode[], groupIds: readonly string[]): GroupCropFrame[] => {
  const frames: GroupCropFrame[] = [];

  for (const groupId of groupIds) {
    const bounds = computeGroupBounds(getGroupMembers(nodes, groupId));

    if (bounds && bounds.width > 0 && bounds.height > 0) {
      frames.push({ bounds, groupId });
    }
  }

  return frames;
};

const memberIdsForGroups = (nodes: AppNode[], groupIds: readonly string[]): Set<string> => {
  const memberIds = new Set<string>();

  for (const groupId of groupIds) {
    for (const id of memberIdSet(nodes, groupId)) {
      memberIds.add(id);
    }
  }

  return memberIds;
};

const cropTargetIdSet = (
  nodes: AppNode[],
  groupIds: readonly string[],
  looseNodeIds: readonly string[],
): Set<string> => {
  const memberIds = memberIdsForGroups(nodes, groupIds);

  for (const id of looseNodeIds) {
    memberIds.add(id);
  }

  return memberIds;
};

/** Keep each composition from being dragged apart while its group frame is up. */
const lockGroupMembersForCrop = (
  nodes: AppNode[],
  groupIds: readonly string[],
  looseNodeIds: readonly string[] = [],
): AppNode[] => {
  const memberIds = cropTargetIdSet(nodes, groupIds, looseNodeIds);

  return nodes.map((node) =>
    memberIds.has(node.id) ? { ...node, draggable: false, selected: true } : { ...node, selected: false },
  );
};

const cropFrameIds = (state: {
  cropGroupIds: string[];
  cropLooseNodeIds: string[];
  cropNodeIds: string[];
}): string[] => {
  if (state.cropGroupIds.length > 0) {
    return [...state.cropGroupIds, ...state.cropLooseNodeIds];
  }

  return state.cropNodeIds;
};

const cropFrameSize = (state: { cropGroupIds: string[]; nodes: AppNode[] }, id: string): PixelSize | null => {
  if (state.cropGroupIds.includes(id)) {
    return groupFramePixelSize(state.nodes, id);
  }

  const node = state.nodes.find((candidate) => candidate.id === id);

  if (!node) {
    return null;
  }

  const size = getNodeDisplaySize(node);

  return size.width > 0 && size.height > 0 ? size : null;
};

const selectGroupMembers = (
  nodes: AppNode[],
  groupIds: readonly string[],
  looseNodeIds: readonly string[] = [],
): AppNode[] => {
  const memberIds = cropTargetIdSet(nodes, groupIds, looseNodeIds);

  return nodes.map((node) => ({ ...node, selected: memberIds.has(node.id) }));
};

export type CropSessionSnapshot = {
  boundingBox: BoundingBox<'normalized'> | null;
  cropBoxes: Record<string, BoundingBox<'normalized'>>;
  cropTargetSize: PixelSize | null;
};

export type SetCanvasModeOptions = {
  cropNodeIds?: string[];
  reselectNode?: boolean;
};

type IsNodeSelectedForCanvasModeParams = {
  canvasMode: CanvasMode;
  nodeId: string;
  prevMode: CanvasMode;
  reselectNode: boolean;
};

const isTransformTargetOf = (mode: CanvasMode, nodeId: string): boolean =>
  'nodeId' in mode &&
  (nodeId === mode.nodeId || ('alsoApplyToNodeIds' in mode && !!mode.alsoApplyToNodeIds?.includes(nodeId)));

/**
 * Leaving a transform reselects its target(s). Entering one keeps the targets selected
 * so the blue ring stays on the image(s) the transform toolbar is attached to. Multi-target
 * modes keep every target selected.
 */
const isNodeSelectedForCanvasMode = ({
  canvasMode,
  nodeId,
  prevMode,
  reselectNode,
}: IsNodeSelectedForCanvasModeParams): boolean => {
  if (canvasMode.mode === 'default') {
    return reselectNode && isTransformTargetOf(prevMode, nodeId);
  }

  return isTransformTargetOf(canvasMode, nodeId);
};

export type ClientSidePreview = {
  nodeId: string;
  blobUrl: string;
  width: number;
  height: number;
};

/** The asset a host handed off to this canvas, offered as the one-click "Save as version" target. */
export type CanvasVersionTarget = {
  assetId: string;
  assetName: string;
  /** Version to return to — the one the canvas was opened from, then the latest one saved from it. */
  clipId: string;
  /**
   * Agent runs started on the asset or on a result made from it. Their results are the ones the
   * canvas proactively offers to save back; anything else made on the canvas is not suggested.
   */
  linkedRunIds: string[];
  /**
   * Media saved back to the asset from this canvas. Tracked by file rather than by node, because
   * edits in place keep the node (and its asset link) but change what would be saved.
   */
  savedMediaUrls: string[];
};

type EditCanvasState = {
  signature: 'aircanvas';
  version: 1;
  activeTool: ActiveTool;
  aspectRatio: number | null;
  boundingBox: BoundingBox<'normalized'> | null;
  /** Per-image crop frames when several images share one output size. */
  cropBoxes: Record<string, BoundingBox<'normalized'>>;
  /**
   * Focused group when the session crops groups as compositions.
   * One group uses `boundingBox` over that group's bounds. Several groups
   * keep a frame each in `cropBoxes`, keyed by group id.
   */
  cropGroupId: string | null;
  /** Every group in the crop session. Empty when crop is per image. */
  cropGroupIds: string[];
  /** Images in a mixed session that keep their own frame beside group frames. */
  cropLooseNodeIds: string[];
  cropNodeIds: string[];
  cropTargetSize: PixelSize | null;
  /** Pre-crop z-indexes so leaving crop can restore stacking. */
  cropZIndexSnapshot: CropZIndexSnapshot | null;
  /** Crop/extend frames may sit outside the unit image square. */
  allowOutsideBounds: boolean;
  canDownload: boolean;
  /** Reads `workspace.canEdit`; save and update affordances for saved sizes/sets stay hidden without it. */
  canEditSavedSizes: boolean;
  canvasMode: CanvasMode;
  clientSidePreview: ClientSidePreview | null;
  isFlattening: boolean;
  isFullscreen: boolean;
  nodes: AppNode[];
  readOnly: boolean;
  /** Gates the saved sizes and sets sections/affordances in the Social variants and video pickers. */
  savedSizesEnabled: boolean;
  showChatInReadOnly: boolean;
  renamingNodeId: string | null;
  rotateFlipPreview: Orientation | null;
  /** Cleared on canvas switch, so it only applies to the canvas the host opened for that asset. */
  versionTarget: CanvasVersionTarget | null;
  /** Written by the host's "Back to asset" pill, which can hide while `versionTarget` stays set. */
  versionTargetBarVisible: boolean;
  canvasId: string | null;
  /**
   * True once this canvas visit has had nodes. Store-backed so an overlay/docked remount
   * cannot restore the scatter hero after the user has already put content on the canvas.
   */
  hasEverHadNodes: boolean;
  /** Host canvas id the `hasEverHadNodes` latch belongs to (`''` while a draft). */
  hasEverHadNodesVisitId: string;
  /** Canvas title, mirrored from the host prop so exports can name their files after the canvas. */
  canvasTitle: string | undefined;
  /** From `syncMutations.workspaceId`; scopes saved-size/set data-access hooks. */
  workspaceId: string | null;
  boardId: string | null;
  boardTitle: string | undefined;
  showGuideGrid: boolean;
  drillDownGroupId: string | null;
  hoveredNodeId: string | null;
  /** Snap-guide lines to render during node resize. Empty when no resize is active. */
  resizeSnapLines: SnapLine[];
  /** When set, the canvas image chat opener skips auto-open so a null-state outcome owns chat setup. */
  nullStateOutcomeChatNodeId: string | null;
  /** Runs smart-resize automatically once the queued image node is on the canvas with its media. */
  pendingAutoSmartResize: PendingAutoSmartResize | null;
  /**
   * Opens the smart-resize dropdown after a gallery handoff or armed rail click.
   * Image/video toolbars consume this when the node is ungrouped; GroupToolbar consumes it
   * when the node is in a selected group. Set after `setCanvasId` so the canvas-switch
   * reset cannot swallow it.
   */
  pendingSmartResizeOpenNodeId: string | null;
  /**
   * Where the smart-resize picker was opened from, and the saved set it preselects, for the next
   * picker mount on this node. Keyed by node so a handoff the picker never consumes cannot apply
   * to some other node.
   */
  pendingSmartResizeHandoff: PendingSmartResizeHandoff | null;
  /**
   * Left-rail tool waiting for a matching node click. Cleared on canvas switch, Escape, or once it runs.
   */
  armedCanvasToolId: CanvasToolRailToolId | null;
  /** Prompt-pill draft. Cleared by the null-state layer each time the hero appears. */
  nullStatePromptDraft: string;
  /** The prompt's "/" skills list is open, so the canvas's Escape handling leaves the key to the list. */
  nullStateSkillSuggestionOpen: boolean;
  /** Canvas the null-state hero last greeted. Store-backed so a panel remount mid-visit cannot greet again or wipe the draft. */
  nullStateGreetedCanvasId: string | null;
  /** Action-first layout: selected outcome id before the user picks an asset. */
  nullStateSelectedOutcomeId: string | null;
  /** Action-first layout: asset held while the user picks an outcome. */
  nullStateSelectedAsset: CanvasProposedImage | null;
  /** Asset whose outcome is completing (nodes inserting, chat opening) — keeps the staged card on screen after the claim clears the selection. */
  nullStateCompletingAsset: CanvasProposedImage | null;
  /** Extra images from a multi-image drop; they land with the held asset and share its action. */
  nullStateStagedCompanions: CanvasProposedImage[];
  flushSync?: () => Promise<void>;
  getEditUploadUrl?: GetEditUploadUrlFn;
  groupMutations?: GroupMutations;
  onSaveToWorkspace?: (nodes: AppNode[], options?: CanvasExportOptions) => void;
  onSaveCompositeToWorkspace?: (params: SaveCompositeParams) => void;
  /** Saves in order as one batch, so several nodes share one result toast. */
  onSaveAsNewVersion?: (nodes: AppNode[], assetId: string, targetBoardId?: string) => Promise<void> | void;
  onPickBoardAssetForVersion?: (node: AppNode, scope: SaveVersionBoardScope | undefined) => void;
  renderSaveVersionBoardScopeChip?: (props: {
    hideAllWorkspace: boolean;
    scope: SaveVersionBoardScope | undefined;
    onScopeChange: (scope: SaveVersionBoardScope | undefined) => void;
  }) => ReactNode;
  /** Opens the canvas asset picker so the user can browse and add assets from a board. */
  onAddFromBoard?: () => void;
  onShareClick?: () => boolean | undefined;
  onSharePreview?: (previewNodeId: string) => boolean | undefined;
  createNodes?: (nodes: AppNode[]) => void;
  updateNodes?: (updates: NodeUpdate[]) => void;
  deleteNodes?: (nodes: AppNode[]) => void;
  zoomToFit?: (params?: ZoomToFitParams) => void;
  /** Moves React Flow's own viewport without animation, for callers outside the ReactFlow tree. */
  applyViewport?: (viewport: Viewport) => void;
  /** Set while Escape should leave the canvas outright, including from inside a tool such as Animate. */
  closeCanvasOnEscape?: () => void;
  zoomToFitIfNeeded?: (params: ZoomToFitIfNeededParams) => void;
  selectionClickCount: number;
  /**
   * Mirror of "any node is selected", written once per selection change by `useSelectionChange`.
   * Nodes read this instead of scanning React Flow's node list, which would be O(n) per node per
   * store update — quadratic across a large canvas.
   */
  hasCanvasSelection: boolean;
};

type CallbackKeys =
  | 'createNodes'
  | 'deleteNodes'
  | 'flushSync'
  | 'getEditUploadUrl'
  | 'groupMutations'
  | 'updateNodes'
  | 'onSaveToWorkspace'
  | 'onSaveCompositeToWorkspace'
  | 'onSaveAsNewVersion'
  | 'onPickBoardAssetForVersion'
  | 'renderSaveVersionBoardScopeChip'
  | 'onAddFromBoard'
  | 'onShareClick'
  | 'onSharePreview';

type EditCanvasActions = {
  addNodes: (nodes: AppNode[], options?: { removeNodeIds?: string[]; unselectExistingNodes?: boolean }) => void;
  clearBoundingBox: () => void;
  moveNodes: (updates: Array<{ id: string; position: { x: number; y: number } }>) => void;
  onNodesChange: (changes: NodeChange<AppNode>[]) => void;
  removeNodes: (nodeIds: string[]) => void;
  removeSmartResizeFormatId: (nodeIds: string[]) => void;
  resizeNodes: (updates: Array<{ id: string; width: number; height: number }>) => void;
  restoreSmartResizeFormatId: (updates: FormatIdStrip[]) => void;
  setActiveTool: (tool: ActiveTool) => void;
  setAspectRatio: (aspectRatio: number | null) => void;
  setBoundingBox: (boundingBox: BoundingBox<'normalized'>) => void;
  setCallbacks: (callbacks: Partial<Pick<EditCanvasState, CallbackKeys>>) => void;
  setCanvasId: (canvasId: string | null) => void;
  setCanvasMode: (canvasMode: CanvasMode, options?: SetCanvasModeOptions) => void;
  setClientSidePreview: (preview: ClientSidePreview | null) => void;
  setCropFocus: (nodeId: string) => void;
  setGroupCropBox: (groupId: string, box: BoundingBox<'normalized'>) => void;
  setCropTargetSize: (size: PixelSize) => void;
  setNodeCropBox: (nodeId: string, box: BoundingBox<'normalized'>) => void;
  setSharedCropFromNode: (nodeId: string, box: BoundingBox<'normalized'>) => void;
  setSharedCropMove: (nodeId: string, box: BoundingBox<'normalized'>) => void;
  restoreCropSession: (snapshot: CropSessionSnapshot) => void;
  setIsFlattening: (isFlattening: boolean) => void;
  setNodes: (nodes: AppNode[], boundingBox?: BoundingBox<'normalized'> | null) => void;
  setFullscreen: (isFullscreen: boolean) => void;
  setCanDownload: (canDownload: boolean) => void;
  setReadOnly: (readOnly: boolean, canDownload?: boolean) => void;
  setShowChatInReadOnly: (showChatInReadOnly: boolean) => void;
  setRenamingNodeId: (nodeId: string | null) => void;
  setVersionTarget: (versionTarget: CanvasVersionTarget | null) => void;
  setVersionTargetBarVisible: (versionTargetBarVisible: boolean) => void;
  setResizeSnapLines: (lines: SnapLine[]) => void;
  setRotateFlipPreview: (preview: Orientation | null) => void;
  updateNodeContent: (
    id: string,
    update: { data: NodeUpdate['data']; position: { x: number; y: number }; width: number; height: number },
  ) => void;
  updateNodeData: (nodeId: string, dataUpdates: Record<string, unknown>) => void;
  updateNodeRemoteId: (localId: string, remoteId: string) => void;
  updateZIndexes: (updates: Array<{ id: string; zIndex: number }>) => void;
  setGroupId: (nodeIds: string[], groupId: string | undefined) => void;
  setDrillDownGroupId: (groupId: string | null) => void;
  setHoveredNodeId: (nodeId: string | null) => void;
  setZoomToFit: (fn: ((params?: ZoomToFitParams) => void) | undefined) => void;
  setApplyViewport: (fn: ((viewport: Viewport) => void) | undefined) => void;
  setCloseCanvasOnEscape: (fn: (() => void) | undefined) => void;
  setZoomToFitIfNeeded: (fn: ((params: ZoomToFitIfNeededParams) => void) | undefined) => void;
  increaseSelectionClick: () => void;
  setHasCanvasSelection: (hasCanvasSelection: boolean) => void;
  setNullStateOutcomeChatNodeId: (nodeId: string | null) => void;
  setPendingAutoSmartResize: (pendingAutoSmartResize: PendingAutoSmartResize | null) => void;
  setPendingSmartResizeOpenNodeId: (nodeId: string | null) => void;
  setPendingSmartResizeHandoff: (pendingSmartResizeHandoff: PendingSmartResizeHandoff | null) => void;
  setArmedCanvasToolId: (armedCanvasToolId: CanvasToolRailToolId | null) => void;
  setNullStatePromptDraft: (draft: string) => void;
  setNullStateSkillSuggestionOpen: (nullStateSkillSuggestionOpen: boolean) => void;
  setHasEverHadNodesVisit: (visit: HasEverHadNodesVisit) => void;
  resetHasEverHadNodesVisit: () => void;
  /**
   * Always drop leftover editor nodes/transients for a new draft. `setCanvasId('')` is a
   * no-op when already on `''`, so opening New Canvas after Edit in canvas would keep
   * the previous draft's nodes and skip the scatter hero.
   */
  resetForNewDraft: () => void;
  setNullStateGreetedCanvasId: (nullStateGreetedCanvasId: string | null) => void;
  setNullStateSelectedOutcomeId: (outcomeId: string | null) => void;
  setNullStateSelectedAsset: (asset: CanvasProposedImage | null) => void;
  setNullStateCompletingAsset: (nullStateCompletingAsset: CanvasProposedImage | null) => void;
  setNullStateStagedCompanions: (companions: CanvasProposedImage[]) => void;
};

const INITIAL_STATE: EditCanvasState = {
  activeTool: 'select',
  allowOutsideBounds: false,
  armedCanvasToolId: null,
  aspectRatio: null,
  boardId: null,
  boardTitle: undefined,
  boundingBox: null,
  canDownload: true,
  canEditSavedSizes: false,
  canvasId: null,
  canvasMode: { mode: 'default' },
  canvasTitle: undefined,
  clientSidePreview: null,
  cropBoxes: {},
  cropGroupId: null,
  cropGroupIds: [],
  cropLooseNodeIds: [],
  cropNodeIds: [],
  cropTargetSize: null,
  cropZIndexSnapshot: null,
  drillDownGroupId: null,
  hasCanvasSelection: false,
  hasEverHadNodes: false,
  hasEverHadNodesVisitId: '',
  hoveredNodeId: null,
  isFlattening: false,
  isFullscreen: false,
  nodes: [],
  nullStateCompletingAsset: null,
  nullStateGreetedCanvasId: null,
  nullStateOutcomeChatNodeId: null,
  nullStatePromptDraft: '',
  nullStateSelectedAsset: null,
  nullStateSelectedOutcomeId: null,
  nullStateSkillSuggestionOpen: false,
  nullStateStagedCompanions: [],
  pendingAutoSmartResize: null,
  pendingSmartResizeHandoff: null,
  pendingSmartResizeOpenNodeId: null,
  readOnly: false,
  renamingNodeId: null,
  resizeSnapLines: [],
  rotateFlipPreview: null,
  savedSizesEnabled: false,
  selectionClickCount: 0,
  showChatInReadOnly: false,
  showGuideGrid: false,
  signature: 'aircanvas',
  version: 1,
  versionTarget: null,
  versionTargetBarVisible: false,
  workspaceId: null,
};

const canvasSwitchReset = (canvasId: string | null): Partial<EditCanvasState> => ({
  activeTool: 'select',
  allowOutsideBounds: false,
  armedCanvasToolId: null,
  aspectRatio: null,
  boundingBox: null,
  canvasId,
  canvasMode: { mode: 'default' },
  clientSidePreview: null,
  cropBoxes: {},
  cropGroupId: null,
  cropGroupIds: [],
  cropLooseNodeIds: [],
  cropNodeIds: [],
  cropTargetSize: null,
  cropZIndexSnapshot: null,
  drillDownGroupId: null,
  hasCanvasSelection: false,
  hasEverHadNodes: false,
  hasEverHadNodesVisitId: canvasId ?? '',
  hoveredNodeId: null,
  isFlattening: false,
  nodes: [],
  nullStateCompletingAsset: null,
  nullStateOutcomeChatNodeId: null,
  nullStatePromptDraft: '',
  nullStateSelectedAsset: null,
  nullStateSelectedOutcomeId: null,
  nullStateStagedCompanions: [],
  pendingAutoSmartResize: null,
  pendingSmartResizeHandoff: null,
  pendingSmartResizeOpenNodeId: null,
  renamingNodeId: null,
  rotateFlipPreview: null,
  showGuideGrid: false,
  versionTarget: null,
});

export const useEditCanvasStore = create<EditCanvasState & EditCanvasActions>((set, get) => ({
  ...INITIAL_STATE,

  addNodes: (newNodes, options) => {
    if (get().readOnly) {
      return;
    }

    const { removeNodeIds, unselectExistingNodes } = options ?? {};
    const maxZIndex = get().nodes.reduce((max, n) => Math.max(max, n.zIndex ?? 0), -1);
    const nodesWithZIndex = newNodes.map((node, i) => ({
      ...node,
      zIndex: node.zIndex ?? maxZIndex + 1 + i,
    }));
    const removeSet = removeNodeIds ? new Set(removeNodeIds) : null;

    set(
      produce((state: EditCanvasState) => {
        if (removeSet) {
          state.nodes = state.nodes.filter((n: AppNode) => !removeSet.has(n.id));
        }
        if (unselectExistingNodes) {
          for (const n of state.nodes) {
            if (n.selected) {
              n.selected = false;
            }
          }
        }
        state.nodes.push(...nodesWithZIndex);
      }),
    );

    get().createNodes?.(nodesWithZIndex);
  },

  clearBoundingBox: () => set({ boundingBox: null }),

  increaseSelectionClick: () => set((state) => ({ selectionClickCount: state.selectionClickCount + 1 })),

  moveNodes: (updates) => {
    set(
      produce((state: EditCanvasState) => {
        for (const update of updates) {
          const node = state.nodes.find((n: AppNode) => n.id === update.id);

          if (node) {
            node.position = update.position;
          }
        }
      }),
    );
    get().updateNodes?.(updates);
  },

  onNodesChange: (changes) =>
    set((state) => {
      // Substitute snap-corrected dimensions/position for the actively resizing
      // node. `<NodeResizer>` calls our resize-snap hook before dispatching its
      // change here, so this is where the snapped values land in the store.
      const interceptedChanges = changes.map((change) => {
        if (change.type === 'dimensions') {
          const target = getResizeSnapTarget(change.id);

          if (target && change.dimensions) {
            return { ...change, dimensions: { height: target.height, width: target.width } };
          }
        } else if (change.type === 'position') {
          const target = getResizeSnapTarget(change.id);

          if (target && change.position) {
            return { ...change, position: { x: target.x, y: target.y } };
          }
        }

        return change;
      });

      const nodes = applyNodeChanges(interceptedChanges, state.nodes);
      let extraState: Partial<EditCanvasState> = {};

      // Sync expanding bounding box node → boundingBox state
      const { canvasMode } = state;

      if (canvasMode.mode === 'extendBackground') {
        const expandingBB = nodes.find((n) => n.type === 'expandingBoundingBox');
        const imageNode = nodes.find((n) => n.id === canvasMode.nodeId);

        if (expandingBB && imageNode) {
          const imageW = typeof imageNode.style?.width === 'number' ? imageNode.style.width : 0;
          const imageH = typeof imageNode.style?.height === 'number' ? imageNode.style.height : 0;
          const bbW = expandingBB.width ?? (typeof expandingBB.style?.width === 'number' ? expandingBB.style.width : 0);
          const bbH =
            expandingBB.height ?? (typeof expandingBB.style?.height === 'number' ? expandingBB.style.height : 0);

          if (imageW > 0 && imageH > 0) {
            extraState = {
              boundingBox: {
                __unit: 'normalized',
                height: bbH / imageH,
                width: bbW / imageW,
                x: (expandingBB.position.x - imageNode.position.x) / imageW,
                y: (expandingBB.position.y - imageNode.position.y) / imageH,
              },
            };
          }
        }
      }

      // Auto-exit drill-down when clicking outside the group (no group member remains selected).
      // Don't re-select members here — the always-on overlay handles selection on the next click.
      if (state.drillDownGroupId) {
        const anyGroupMemberSelected = nodes.some((n) => n.groupId === state.drillDownGroupId && n.selected);

        if (!anyGroupMemberSelected) {
          extraState = { ...extraState, drillDownGroupId: null };
        }
      }

      return { ...extraState, nodes };
    }),

  removeNodes: (nodeIds) => {
    const idsToRemove = new Set(nodeIds);
    const [removedNodes, remainingNodes] = partition(get().nodes, (n) => idsToRemove.has(n.id));
    set({ nodes: remainingNodes });
    get().deleteNodes?.(removedNodes);
  },

  removeSmartResizeFormatId: (nodeIds) => {
    const idSet = new Set(nodeIds);

    set(
      produce((state: EditCanvasState) => {
        for (const node of state.nodes) {
          if (idSet.has(node.id) && node.type === 'image') {
            Reflect.deleteProperty(node.data, 'smartResizeFormatId');
          }
        }
      }),
    );

    const updates: NodeUpdate[] = get()
      .nodes.filter((node) => idSet.has(node.id))
      .flatMap((node) => {
        const nodeData = getNodeData(node);

        return nodeData ? [{ data: nodeData.data, id: node.id }] : [];
      });

    get().updateNodes?.(updates);
  },

  resetForNewDraft: () => {
    useLocalizationStore.getState().close();
    // Draft-to-draft keeps canvasId `''`, so useCanvasForRightPanel's canvas-switch
    // cleanup never runs. Leftover undo would restore the abandoned draft's nodes.
    useCommandHistory.getState().clear();
    set(canvasSwitchReset(''));
  },
  resetHasEverHadNodesVisit: () => set({ hasEverHadNodes: false, hasEverHadNodesVisitId: '' }),

  resizeNodes: (updates) => {
    set(
      produce((state: EditCanvasState) => {
        for (const update of updates) {
          const node = state.nodes.find((n: AppNode) => n.id === update.id);

          if (node) {
            node.style = { ...node.style, height: update.height, width: update.width };
            // Clear ReactFlow's measured/direct dimensions so the updated style
            // values take effect on the next render. Without this, undoing a
            // resize leaves the node visually at its post-resize size because
            // measured/width/height — set by <NodeResizer> via applyNodeChanges
            // — outrank style. Mirrors the pattern in updateNodeContent below.
            node.measured = undefined;
            node.width = undefined;
            node.height = undefined;
          }
        }
      }),
    );
    get().updateNodes?.(updates);
  },

  restoreCropSession: (snapshot) => set(snapshot),

  restoreSmartResizeFormatId: (updates) => {
    const formatIdByNodeId = new Map(updates.map((u) => [u.nodeId, u.formatId]));

    set(
      produce((state: EditCanvasState) => {
        for (const node of state.nodes) {
          const formatId = formatIdByNodeId.get(node.id);

          if (formatId !== undefined && node.type === 'image') {
            node.data.smartResizeFormatId = formatId;
          }
        }
      }),
    );

    const syncUpdates: NodeUpdate[] = get()
      .nodes.filter((node) => formatIdByNodeId.has(node.id))
      .flatMap((node) => {
        const nodeData = getNodeData(node);

        return nodeData ? [{ data: nodeData.data, id: node.id }] : [];
      });

    get().updateNodes?.(syncUpdates);
  },

  setActiveTool: (tool) =>
    set(
      produce((state: EditCanvasState) => {
        state.activeTool = tool;
        state.armedCanvasToolId = null;
        if (tool === 'hand') {
          for (const node of state.nodes) {
            node.selected = false;
          }
        }
      }),
    ),

  setApplyViewport: (fn) => set({ applyViewport: fn }),

  setArmedCanvasToolId: (armedCanvasToolId) => set({ armedCanvasToolId }),

  setAspectRatio: (aspectRatio) => set({ aspectRatio }),

  setBoundingBox: (boundingBox) =>
    set((state) => {
      const isSingleGroup = state.cropGroupIds.length === 1 && state.cropLooseNodeIds.length === 0;

      if (state.canvasMode.mode !== 'crop' || isSingleGroup) {
        return { boundingBox };
      }

      if (state.cropGroupId && state.cropGroupIds.includes(state.cropGroupId)) {
        return {
          boundingBox,
          cropBoxes: { ...state.cropBoxes, [state.cropGroupId]: boundingBox },
        };
      }

      return {
        boundingBox,
        cropBoxes: { ...state.cropBoxes, [state.canvasMode.nodeId]: boundingBox },
      };
    }),

  setCallbacks: (callbacks) => set(callbacks),

  setCanDownload: (canDownload) => set({ canDownload }),

  setCanvasId: (canvasId) => {
    const prevCanvasId = useEditCanvasStore.getState().canvasId;

    if (canvasId !== prevCanvasId) {
      // No previous canvas (null on first load, '' on a draft) — assigning an id
      // is not a switch. Skip the reset so a host that already seeded nodes
      // (public share hydration) or a mid-commit held asset is not wiped.
      if (!prevCanvasId && canvasId) {
        set({ canvasId, hasEverHadNodesVisitId: canvasId });
        return;
      }

      // Reset all transient state when switching canvases to avoid stale
      // mode, selection, or preview state leaking across canvases. Nodes
      // clear here so a canvas switch is one update (id + empty nodes), not a
      // mass delete of the previous canvas's sources.
      useLocalizationStore.getState().close();
      set(canvasSwitchReset(canvasId));
    }
  },

  setCanvasMode: (canvasMode, options) => {
    if (get().readOnly && canvasMode.mode !== 'default') {
      return;
    }
    const reselectNode = options?.reselectNode ?? true;
    const prevMode = useEditCanvasStore.getState().canvasMode;
    const prevConfig = prevMode.mode !== 'default' ? TRANSFORM_STORE_CONFIGS[prevMode.mode] : undefined;
    const nextConfig = canvasMode.mode !== 'default' ? TRANSFORM_STORE_CONFIGS[canvasMode.mode] : undefined;

    // Note: transitioning directly between two isolated-history modes is not supported
    const entering = nextConfig?.isolateCommandHistory;
    const leaving = prevConfig?.isolateCommandHistory;

    if (entering) {
      useCommandHistory.getState().pushStack();
    }
    if (leaving) {
      useCommandHistory.getState().popStack();
    }

    // Reset to select tool when entering a transform mode
    if (canvasMode.mode !== 'default') {
      set({ activeTool: 'select', armedCanvasToolId: null });
    }

    set((state) => {
      // Remove existing expanding bounding box nodes and reset per-node overrides
      let nodes: AppNode[] = state.nodes
        .filter((obj) => obj.type !== 'expandingBoundingBox')
        .map(
          (obj): AppNode => ({
            ...obj,
            draggable: undefined,
            selected: isNodeSelectedForCanvasMode({
              canvasMode,
              nodeId: obj.id,
              prevMode,
              reselectNode,
            }),
          }),
        );

      // Add expanding bounding box when entering extendBackground mode
      if (canvasMode.mode === 'extendBackground') {
        const imageNode = nodes.find((o) => o.id === canvasMode.nodeId);

        if (imageNode) {
          const imageW = typeof imageNode.style?.width === 'number' ? imageNode.style.width : 0;
          const imageH = typeof imageNode.style?.height === 'number' ? imageNode.style.height : 0;

          // Lock the target image in place during extend background
          nodes = nodes.map((obj): AppNode => (obj.id === canvasMode.nodeId ? { ...obj, draggable: false } : obj));

          const expandingBoundingBox: AppNode = {
            data: { imageNodeId: canvasMode.nodeId },
            draggable: false,
            id: 'expanding-bounding-box',
            position: { x: imageNode.position.x, y: imageNode.position.y },
            style: { height: imageH, width: imageW },
            type: 'expandingBoundingBox',
          };

          nodes = [...nodes, expandingBoundingBox];
        }
      }

      let cropSession: ReturnType<typeof buildCropSession> | null = null;
      let cropZIndexSnapshot = state.cropZIndexSnapshot;
      let cropGroupId: string | null = null;
      let cropGroupIds: string[] = [];
      let cropLooseNodeIds: string[] = [];

      if (canvasMode.mode === 'crop') {
        const cropNodeIds = options?.cropNodeIds ?? [canvasMode.nodeId];
        const partition = partitionCropTargets(nodes, cropNodeIds);
        const frames = groupCropFrames(nodes, partition?.groupIds ?? []);
        const looseNodes = (partition?.looseNodeIds ?? []).flatMap((id) => {
          const node = nodes.find((candidate) => candidate.id === id);

          if (!node) {
            return [];
          }

          const size = getNodeDisplaySize(node);

          return size.width > 0 && size.height > 0 ? [{ id, size }] : [];
        });
        const focusedFrame =
          frames.find((frame) => frame.groupId === nodes.find((node) => node.id === canvasMode.nodeId)?.groupId) ??
          (looseNodes.length === 0 ? frames[0] : undefined);

        if (focusedFrame && frames.length === 1 && looseNodes.length === 0) {
          cropGroupId = focusedFrame.groupId;
          cropGroupIds = [focusedFrame.groupId];
          cropSession = buildGroupCropSession(cropNodeIds, focusedFrame.bounds);
          cropZIndexSnapshot = null;
          nodes = lockGroupMembersForCrop(nodes, cropGroupIds);
        } else if (focusedFrame && frames.length > 1 && looseNodes.length === 0) {
          cropGroupId = focusedFrame.groupId;
          cropGroupIds = frames.map((frame) => frame.groupId);
          cropSession = buildMultiGroupCropSession(cropNodeIds, frames, focusedFrame.groupId);
          cropZIndexSnapshot = null;
          nodes = lockGroupMembersForCrop(nodes, cropGroupIds);
        } else if (frames.length >= 1 && looseNodes.length > 0) {
          const looseFocus = looseNodes.find((node) => node.id === canvasMode.nodeId) ?? looseNodes[0];
          cropGroupIds = frames.map((frame) => frame.groupId);
          cropLooseNodeIds = looseNodes.map((node) => node.id);
          cropGroupId = focusedFrame?.groupId ?? null;
          const focusedSize = focusedFrame
            ? { height: Math.ceil(focusedFrame.bounds.height), width: Math.ceil(focusedFrame.bounds.width) }
            : (looseFocus?.size ?? null);
          cropSession = buildMixedCropSession(cropNodeIds, frames, looseNodes, focusedSize);
          cropZIndexSnapshot = null;
          nodes = lockGroupMembersForCrop(nodes, cropGroupIds, cropLooseNodeIds);
        } else {
          cropSession = buildCropSession(nodes, cropNodeIds, canvasMode.nodeId);
          cropZIndexSnapshot = snapshotCropZIndexes(cropZIndexSnapshot, nodes, cropSession.cropNodeIds);
          nodes = applyCropFocusZIndex(nodes, cropSession.cropNodeIds, canvasMode.nodeId, cropZIndexSnapshot);
        }
      } else {
        const previousGroupIds = state.cropGroupIds;
        const previousLooseNodeIds = state.cropLooseNodeIds;
        nodes = restoreCropZIndexes(nodes, cropZIndexSnapshot);
        cropZIndexSnapshot = null;

        if (
          reselectNode &&
          (previousGroupIds.length > 0 || previousLooseNodeIds.length > 0) &&
          canvasMode.mode === 'default'
        ) {
          nodes = selectGroupMembers(nodes, previousGroupIds, previousLooseNodeIds);
        }
      }

      const nextBoundingBox =
        cropSession?.boundingBox ??
        (nextConfig?.usesBoundingBox ? (nextConfig.usesBoundingBox.initialBox ?? null) : null);

      return {
        allowOutsideBounds: !!nextConfig?.usesBoundingBox && !!nextConfig.usesBoundingBox.allowOutsideBounds,
        aspectRatio: null,
        boundingBox: nextBoundingBox,
        canvasMode,
        cropBoxes: cropSession?.cropBoxes ?? {},
        cropGroupId,
        cropGroupIds,
        cropLooseNodeIds,
        cropNodeIds: cropSession?.cropNodeIds ?? [],
        cropTargetSize: cropSession?.cropTargetSize ?? null,
        cropZIndexSnapshot,
        nodes,
        rotateFlipPreview: null,
        showGuideGrid: !!nextConfig?.usesBoundingBox && !!nextConfig.usesBoundingBox.showGuideGrid,
      };
    });
  },

  setClientSidePreview: (preview) => set({ clientSidePreview: preview }),

  setCloseCanvasOnEscape: (fn) => set({ closeCanvasOnEscape: fn }),

  setCropFocus: (nodeId) => {
    const state = get();

    if (state.canvasMode.mode !== 'crop') {
      return;
    }

    if (state.cropGroupIds.includes(nodeId) && (state.cropGroupIds.length > 1 || state.cropLooseNodeIds.length > 0)) {
      if (state.cropGroupId === nodeId) {
        return;
      }

      const members = getGroupMembers(state.nodes, nodeId);
      const bounds = computeGroupBounds(members);
      const focusNode = members.find((node) => state.cropNodeIds.includes(node.id)) ?? members[0];

      if (!bounds || !focusNode || bounds.width <= 0 || bounds.height <= 0) {
        return;
      }

      set({
        boundingBox: state.cropBoxes[nodeId] ?? state.boundingBox,
        canvasMode: { mode: 'crop', nodeId: focusNode.id },
        cropGroupId: nodeId,
        cropTargetSize: { height: Math.ceil(bounds.height), width: Math.ceil(bounds.width) },
      });
      return;
    }

    if (state.cropLooseNodeIds.includes(nodeId)) {
      if (state.canvasMode.nodeId === nodeId && state.cropGroupId === null) {
        return;
      }

      const node = state.nodes.find((candidate) => candidate.id === nodeId);
      const size = node ? getNodeDisplaySize(node) : null;

      set({
        boundingBox: state.cropBoxes[nodeId] ?? state.boundingBox,
        canvasMode: { mode: 'crop', nodeId },
        cropGroupId: null,
        cropTargetSize: size && size.width > 0 && size.height > 0 ? size : state.cropTargetSize,
      });
      return;
    }

    if (state.cropGroupId || !state.cropNodeIds.includes(nodeId)) {
      return;
    }

    if (state.canvasMode.nodeId === nodeId) {
      return;
    }

    set({
      boundingBox: state.cropBoxes[nodeId] ?? state.boundingBox,
      canvasMode: { mode: 'crop', nodeId },
      nodes: applyCropFocusZIndex(state.nodes, state.cropNodeIds, nodeId, state.cropZIndexSnapshot).map((node) => ({
        ...node,
        selected: node.id === nodeId,
      })),
    });
  },

  setCropTargetSize: (size) => {
    const state = get();

    if (state.cropGroupIds.length > 0 || size.width < 1 || size.height < 1) {
      return;
    }

    const nodeSizes: Record<string, PixelSize> = {};

    for (const id of state.cropNodeIds) {
      const node = state.nodes.find((candidate) => candidate.id === id);

      if (node) {
        nodeSizes[id] = getNodeDisplaySize(node);
      }
    }

    const cropBoxes = syncCropBoxesToTargetSize(state.cropBoxes, nodeSizes, size);
    const primaryId = state.canvasMode.mode === 'crop' ? state.canvasMode.nodeId : undefined;

    set({
      boundingBox: primaryId ? (cropBoxes[primaryId] ?? state.boundingBox) : state.boundingBox,
      cropBoxes,
      cropTargetSize: size,
    });
  },

  setDrillDownGroupId: (drillDownGroupId) => set({ drillDownGroupId }),

  setFullscreen: (isFullscreen) => set({ isFullscreen }),

  setGroupCropBox: (groupId, box) => {
    const state = get();

    if (!state.cropGroupIds.includes(groupId)) {
      return;
    }

    const cropBoxes = { ...state.cropBoxes, [groupId]: box };

    if (state.cropGroupId !== groupId) {
      set({ cropBoxes });
      return;
    }

    set({ boundingBox: box, cropBoxes });
  },

  setGroupId: (nodeIds, groupId) => {
    const idSet = new Set(nodeIds);

    set(
      produce((state: EditCanvasState) => {
        for (const node of state.nodes) {
          if (idSet.has(node.id)) {
            node.groupId = groupId;
          }
        }
      }),
    );
  },
  setHasCanvasSelection: (hasCanvasSelection) => set({ hasCanvasSelection }),
  setHasEverHadNodesVisit: ({ hasEverHadNodes, visitCanvasId }) =>
    set({ hasEverHadNodes, hasEverHadNodesVisitId: visitCanvasId }),

  setHoveredNodeId: (hoveredNodeId) => set({ hoveredNodeId }),

  setIsFlattening: (isFlattening) => set({ isFlattening }),

  setNodeCropBox: (nodeId, box) =>
    set((state) => ({
      boundingBox: state.canvasMode.mode === 'crop' && state.canvasMode.nodeId === nodeId ? box : state.boundingBox,
      cropBoxes: { ...state.cropBoxes, [nodeId]: box },
    })),

  setNodes: (nodes, boundingBox) => set(boundingBox !== undefined ? { boundingBox, nodes } : { nodes }),
  setNullStateCompletingAsset: (nullStateCompletingAsset) => set({ nullStateCompletingAsset }),
  setNullStateGreetedCanvasId: (nullStateGreetedCanvasId) => set({ nullStateGreetedCanvasId }),

  setNullStateOutcomeChatNodeId: (nullStateOutcomeChatNodeId) => set({ nullStateOutcomeChatNodeId }),

  setNullStatePromptDraft: (nullStatePromptDraft) => set({ nullStatePromptDraft }),

  setNullStateSelectedAsset: (nullStateSelectedAsset) => set({ nullStateSelectedAsset }),

  setNullStateSelectedOutcomeId: (nullStateSelectedOutcomeId) => set({ nullStateSelectedOutcomeId }),

  setNullStateSkillSuggestionOpen: (nullStateSkillSuggestionOpen) => set({ nullStateSkillSuggestionOpen }),

  setNullStateStagedCompanions: (nullStateStagedCompanions) => set({ nullStateStagedCompanions }),

  setPendingAutoSmartResize: (pendingAutoSmartResize) => set({ pendingAutoSmartResize }),
  setPendingSmartResizeHandoff: (pendingSmartResizeHandoff) => set({ pendingSmartResizeHandoff }),
  setPendingSmartResizeOpenNodeId: (pendingSmartResizeOpenNodeId) => set({ pendingSmartResizeOpenNodeId }),

  setReadOnly: (readOnly, canDownload) => {
    set({ readOnly });
    get().setActiveTool(readOnly && !canDownload ? 'hand' : 'select');
  },

  setRenamingNodeId: (nodeId) => set({ renamingNodeId: nodeId }),

  setResizeSnapLines: (lines) => set({ resizeSnapLines: lines }),

  setRotateFlipPreview: (preview) => set({ rotateFlipPreview: preview }),

  setSharedCropFromNode: (nodeId, box) => {
    const state = get();
    const frameIds = cropFrameIds(state);
    const singleGroup = state.cropGroupIds.length === 1 && state.cropLooseNodeIds.length === 0;

    if (singleGroup || frameIds.length < 2) {
      state.setBoundingBox(box);
      return;
    }

    const sourceId = frameIds.includes(nodeId) ? nodeId : (state.cropGroupId ?? nodeId);
    const sourceSize = cropFrameSize(state, sourceId);

    if (!sourceSize) {
      return;
    }

    const target = targetSizeFromBox(box, sourceSize);

    if (target.width < 1 || target.height < 1) {
      return;
    }

    const nodeSizes: Record<string, PixelSize> = {};

    for (const id of frameIds) {
      const size = cropFrameSize(state, id);

      if (size) {
        nodeSizes[id] = size;
      }
    }

    const previousBox = state.cropBoxes[sourceId] ?? state.boundingBox;
    // Pin the edges that did not move so a left-handle drag does not crop the sibling from both sides.
    const anchor = previousBox
      ? inferCropBoxAnchor({ image: sourceSize, next: box, previous: previousBox })
      : CENTER_CROP_BOX_ANCHOR;
    const cropBoxes = {
      ...syncCropBoxesToTargetSize(state.cropBoxes, nodeSizes, target, anchor),
      [sourceId]: box,
    };

    set({
      boundingBox: box,
      cropBoxes,
      cropTargetSize: target,
    });
  },

  setSharedCropMove: (nodeId, box) => {
    const state = get();
    const frameIds = cropFrameIds(state);

    if (frameIds.length < 2) {
      state.setBoundingBox(box);
      return;
    }

    const sourceSize = cropFrameSize(state, nodeId);
    const previous = state.cropBoxes[nodeId] ?? state.boundingBox;

    if (!sourceSize || !previous || sourceSize.width < 1 || sourceSize.height < 1) {
      return;
    }

    const dx = (box.x - previous.x) * sourceSize.width;
    const dy = (box.y - previous.y) * sourceSize.height;
    const cropBoxes = { ...state.cropBoxes, [nodeId]: box };

    for (const id of frameIds) {
      if (id === nodeId) {
        continue;
      }

      const current = state.cropBoxes[id];
      const size = cropFrameSize(state, id);

      if (!current || !size || size.width < 1 || size.height < 1) {
        continue;
      }

      cropBoxes[id] = {
        __unit: 'normalized',
        height: current.height,
        width: current.width,
        x: current.x + dx / size.width,
        y: current.y + dy / size.height,
      };
    }

    set({ boundingBox: box, cropBoxes });
  },

  setShowChatInReadOnly: (showChatInReadOnly) => set({ showChatInReadOnly }),

  setVersionTarget: (versionTarget) => set({ versionTarget }),

  setVersionTargetBarVisible: (versionTargetBarVisible) => set({ versionTargetBarVisible }),

  setZoomToFit: (fn) => set({ zoomToFit: fn }),

  setZoomToFitIfNeeded: (fn) => set({ zoomToFitIfNeeded: fn }),

  updateNodeContent: (id, update) => {
    set((state) => ({
      nodes: state.nodes.map((node) => {
        if (node.id !== id) {
          return node;
        }

        return produce(node, (draft) => {
          // Replace data entirely (not merge) so stale keys like isProcessing are dropped
          for (const key of Object.keys(draft.data)) {
            if (!(key in Object(update.data))) {
              Reflect.deleteProperty(draft.data, key);
            }
          }

          Object.assign(draft.data, update.data);
          draft.position = update.position;
          draft.style = { ...draft.style, height: update.height, width: update.width };
          // Clear ReactFlow's measured/direct dimensions so it uses the updated style values
          draft.measured = undefined;
          draft.width = undefined;
          draft.height = undefined;
        });
      }),
    }));
    get().updateNodes?.([
      {
        data: update.data,
        height: update.height,
        id,
        position: update.position,
        width: update.width,
      },
    ]);
  },

  updateNodeData: (nodeId, dataUpdates) =>
    set((state) => ({
      nodes: state.nodes.map((obj) => {
        if (obj.id !== nodeId) {
          return obj;
        }

        return produce(obj, (draft) => {
          Object.assign(draft.data, dataUpdates);
        });
      }),
    })),

  updateNodeRemoteId: (localId, remoteId) =>
    set(
      produce((state: EditCanvasState) => {
        const node = state.nodes.find((n: AppNode) => n.id === localId);

        if (node) {
          node.remoteId = remoteId;
        }
      }),
    ),

  updateZIndexes: (updates) => {
    const updateMap = new Map(updates.map((u) => [u.id, u.zIndex]));
    set(
      produce((state: EditCanvasState) => {
        for (const node of state.nodes) {
          const newZIndex = updateMap.get(node.id);

          if (newZIndex !== undefined) {
            node.zIndex = newZIndex;
          }
        }
      }),
    );
    get().updateNodes?.(updates.map((u) => ({ id: u.id, zIndex: u.zIndex })));
  },
}));

// Let localization utilities read/update canvas nodes without importing this store (avoids a cycle).
bindEditCanvasBridge({
  getNodes: () => useEditCanvasStore.getState().nodes,
  removeNodes: (nodeIds) => useEditCanvasStore.getState().removeNodes(nodeIds),
  resetCanvasMode: () => useEditCanvasStore.getState().setCanvasMode({ mode: 'default' }),
  updateNodeData: (nodeId, data) => useEditCanvasStore.getState().updateNodeData(nodeId, data),
});
